/**
 * Load only the languages this till actually needs.
 *
 * Two catalogues at most: Czech, because it is the fallback and a missing
 * string must never surface as an English one in front of a Czech server, and
 * the negotiated language if it is something else.
 *
 * The chunks are precached by the service worker alongside everything else, so
 * "lazy" here means "not in the startup chunk", not "needs a network". A till
 * that has never been online since its last update has them already.
 *
 * The AVAILABLE list is static and tiny — four strings — because negotiation
 * has to happen before anything is fetched. Importing the catalogues to find
 * out which exist would defeat the whole arrangement.
 */

import { negotiateLocale, type Catalog } from "@gf/kernel/i18n";

/** Kept in step with the files beside this one by `locales.spec.ts`. */
export const AVAILABLE_LOCALES = ["cs", "en", "uk", "sk"] as const;

export type AvailableLocale = (typeof AVAILABLE_LOCALES)[number];

/**
 * Czech, not English.
 *
 * This is a Czech product, and an English fallback in front of a Czech server
 * is worse than a missing string — it looks like a different app for one line.
 */
export const FALLBACK_LOCALE = "cs" satisfies AvailableLocale;

const CATALOGUES: Record<AvailableLocale, () => Promise<{ default?: Catalog } & Record<string, unknown>>> = {
  cs: () => import("./cs"),
  en: () => import("./en"),
  uk: () => import("./uk"),
  sk: () => import("./sk"),
};

const load = async (locale: AvailableLocale): Promise<Catalog> => {
  const module = await CATALOGUES[locale]();
  return module[locale] as Catalog;
};

export interface NegotiatedCatalogs {
  readonly locale: AvailableLocale;
  readonly catalogs: readonly Catalog[];
}

/**
 * Pick a language from the device's preferences and fetch what it needs.
 *
 * Returns the fallback FIRST and the chosen language second when they differ,
 * which is the order `Translator` resolves in — so a key present in Czech and
 * missing in Ukrainian answers in Czech rather than not at all.
 */
export async function loadCatalogs(
  languages: readonly string[],
  /**
   * The DEPLOYMENT's language, which outranks the device's preferences.
   *
   * The browser is the wrong authority for a till. A Czech venue's handheld
   * should be in Czech whatever the tablet's system language happens to be —
   * and it frequently is not Czech, because the device was bought configured,
   * or because the last person to hold it changed it. Leaving it to
   * `navigator.languages` put a till reading "Sign in" in front of a Czech
   * server, and the money formatted as "CZK 294.00" rather than "294,00 Kč".
   *
   * The device's own preference still decides when the deployment says nothing,
   * which is what keeps a Ukrainian cook's screen Ukrainian.
   */
  deployed?: string | undefined,
): Promise<NegotiatedCatalogs> {
  const pinned =
    deployed && (AVAILABLE_LOCALES as readonly string[]).includes(deployed)
      ? (deployed as AvailableLocale)
      : null;

  const locale =
    pinned ??
    (negotiateLocale(
      [...languages],
      [...AVAILABLE_LOCALES],
      FALLBACK_LOCALE,
    ) as AvailableLocale);

  const fallback = await load(FALLBACK_LOCALE);
  if (locale === FALLBACK_LOCALE) return { locale, catalogs: [fallback] };

  return { locale, catalogs: [fallback, await load(locale)] };
}

/**
 * The deferred catalogue — the drawer's and the receipt's words.
 *
 * Same shape as the eager loader above and the same reasoning, one level
 * further along: two screens that are already lazily imported had their COPY in
 * the startup chunk, which is four kilobytes a till downloads at the start of
 * every shift to use twice a day.
 *
 * IDEMPOTENT AND CHEAP TO CALL AGAIN. `Translator.extend` merges, the dynamic
 * import is cached by the module system, and the screens that need it may be
 * opened in either order or several times a shift. A caller must not have to
 * remember whether it has already asked.
 */
const DEFERRED: Record<AvailableLocale, () => Promise<Record<string, unknown>>> = {
  cs: () => import("./deferred.cs"),
  en: () => import("./deferred.en"),
  uk: () => import("./deferred.uk"),
  sk: () => import("./deferred.sk"),
};

export async function loadDeferredCatalogs(
  locale: string,
): Promise<readonly Catalog[]> {
  /**
   * CZECH ALWAYS, plus the active language when it is something else — exactly
   * what `negotiateCatalogs` does for the eager half. A Ukrainian cook missing
   * one drawer string must see Czech, which is the fallback; seeing the raw key
   * would look like a broken screen.
   */
  const wanted = new Set<AvailableLocale>([FALLBACK_LOCALE]);
  if ((AVAILABLE_LOCALES as readonly string[]).includes(locale)) {
    wanted.add(locale as AvailableLocale);
  }

  const out: Catalog[] = [];
  for (const want of wanted) {
    const module = await DEFERRED[want]();
    out.push(module[want] as Catalog);
  }
  return out;
}
